/**
 * Identité produit centralisée (Q95, §16.5).
 * « MyEquip » est le nom officiel de la plateforme depuis le 25 juillet 2026.
 * La personnalisation par tenant (logo/couleurs/documents — Q102) reste
 * indépendante de l'identité de la plateforme.
 */
export const PLATFORM_BRANDING = {
  /** Nom officiel du service. */
  productName: 'MyEquip',
  /** Domaine par défaut ; surchargé par APP_BASE_URL au runtime. */
  defaultDomain: 'myequip.local',
  /** Expéditeur email par défaut ; surchargé par SMTP_FROM. */
  defaultEmailSender: 'no-reply@myequip.local',
  /** Couleurs par défaut de la plateforme (thème « métier moderne premium »). */
  colors: {
    primary: '#0b4f6c',
    primaryDark: '#073b4c',
    accent: '#00a896',
    danger: '#c0392b',
    warning: '#b9770e',
    success: '#1e8449',
  },
} as const;

/** Personnalisation par entreprise (stockée dans companies.branding — Q102). */
export interface TenantBranding {
  displayName?: string;
  websiteUrl?: string;
  logoFileId?: string;
  /** Logo servi tel quel (URL absolue https ou data:image) — validé avant usage. */
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  documentFooter?: string;
  emailSignature?: string;
  replyToEmail?: string;
  documentAddress?: string;
  documentEmail?: string;
  /** Mentions de paiement des factures ; priment sur les valeurs historiques de l'entreprise. */
  paymentTerms?: string;
  latePenalty?: string;
  fixedIndemnity?: string;
  logoWidthMm?: number;
  logoHeightMm?: number;
  logoAlignment?: 'left' | 'center' | 'right';
  /** Mise en page : épurée (« modern »), classique, ou compacte pour les longs documents. */
  documentStyle?: DocumentStyle;
  /** Taille du logo sur les documents (12, 18 ou 24 mm de haut). Prime sur les mm. */
  logoSize?: LogoSize;
  documentPhone?: string;

  // --- Mentions légales de l'entreprise (12/10/2026) ---------------------------------
  /** Forme juridique : « SAS, société par actions simplifiée »… */
  legalForm?: string;
  /** Capital social, tel qu'il doit être imprimé (« 500,00 € »). */
  shareCapital?: string;
  /** Immatriculation : « RCS Bobigny 990 665 994 », « RM 93 »… */
  registration?: string;
  /** N° de TVA intracommunautaire de l'entreprise. */
  vatNumber?: string;
  /** Code NAF / APE. */
  apeCode?: string;
  /** Assurance professionnelle (obligatoire pour les artisans du bâtiment). */
  insurance?: string;

  // --- Paiement -------------------------------------------------------------------
  paymentMethod?: string;
  bankName?: string;
  iban?: string;
  bic?: string;
  /** Rappeler de citer le numéro de facture dans le libellé du virement. */
  showPaymentReference?: boolean;
  /** Escompte pour paiement anticipé ; sans réglage, « Pas d’escompte… » sur les factures professionnelles. */
  discountTerms?: string;

  // --- Textes ---------------------------------------------------------------------
  /** Type de vente imprimé sur la facture (catégorie de l'opération). */
  saleType?: SaleType;
  /** Mention spéciale imprimée sur chaque facture. */
  specialMention?: string;
  /** Conditions imprimées sur chaque devis. */
  quoteTerms?: string;

  // --- QR codes et étiquettes des machines (13/10/2026) ------------------------------
  /** Couleur des modules du QR code ; assez sombre pour rester lisible sur fond blanc. */
  qrColor?: string;
  /** Logo de l'entreprise au centre du QR code (correction d'erreurs maximale). */
  qrLogo?: boolean;
  /** Accroche de l'étiquette (« Une panne ? »). */
  qrHeadline?: string;
  /** Invitation à scanner (« Scannez-moi »). */
  qrCallToAction?: string;
  /** Imprimer le téléphone de l'entreprise sous le QR code. */
  qrShowPhone?: boolean;
  /** Format d'étiquette proposé par défaut ; chaque appareil peut en choisir un autre. */
  qrLabelFormat?: QrLabelFormatId;
}

/** Formats d'étiquette QR : ceux des rouleaux courants. */
/**
 * Escompte imprimé sur une facture à un client professionnel (C. com. L441-9) quand l'entreprise
 * n'a rien réglé : la loi exige la mention, même pour dire qu'il n'y en a pas. Modifiable dans
 * les réglages de facturation (`discountTerms`) ; reporté dans le Factur-X (note AAB).
 */
export const DEFAULT_DISCOUNT_TERMS = 'Pas d’escompte pour paiement anticipé.';

export const QR_LABEL_FORMAT_IDS = ['rouleau-62', 'carre-50', 'dymo-54', 'grand-100'] as const;
export type QrLabelFormatId = (typeof QR_LABEL_FORMAT_IDS)[number];
export const QR_TEXT_MAX = 32;
/**
 * Longueur maximale de chaque texte de l'identité visuelle : une seule table
 * pour le schéma de l'API, l'assainissement à la lecture et les champs de
 * l'écran, qui n'acceptent donc jamais ce que le serveur refuse.
 */
export const BRANDING_TEXT_LIMITS = {
  displayName: 80,
  websiteUrl: 2048,
  documentFooter: 500,
  emailSignature: 500,
  documentAddress: 500,
  documentPhone: 30,
  paymentTerms: 500,
  latePenalty: 500,
  fixedIndemnity: 500,
  legalForm: 80,
  shareCapital: 40,
  registration: 120,
  vatNumber: 30,
  apeCode: 10,
  insurance: 500,
  paymentMethod: 80,
  bankName: 80,
  iban: 50,
  bic: 20,
  discountTerms: 300,
  specialMention: 1000,
  quoteTerms: 1000,
  qrColor: 7,
  qrHeadline: QR_TEXT_MAX,
  qrCallToAction: QR_TEXT_MAX,
} as const;
/** Contraste minimal des modules sur le fond blanc pour qu'un téléphone lise le code. */
export const QR_MIN_CONTRAST = 4.5;
/** Part de la largeur du code que le logo central peut couvrir, marge blanche comprise. */
export const QR_LOGO_MAX_RATIO = 0.24;
export const QR_DEFAULTS = {
  color: '#000000',
  headline: 'Une panne ?',
  callToAction: 'Scannez-moi',
} as const;

/** Contraste WCAG d'une couleur `#rrggbb` (ou `#rgb`) sur du blanc ; 1 si illisible. */
export function contrastWithWhite(color: string): number {
  if (!isSafeColor(color)) return 1;
  const hex = color.length === 4 ? [...color.slice(1)].map((c) => c + c).join('') : color.slice(1);
  const [r, g, b] = [0, 2, 4].map((index) => {
    const channel = parseInt(hex.slice(index, index + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 1.05 / (0.2126 * r + 0.7152 * g + 0.0722 * b + 0.05);
}

/** Couleur de QR code acceptée : `#rrggbb`, assez sombre pour être scannée sur blanc. */
export function isScannableQrColor(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^#[0-9a-f]{6}$/i.test(value) &&
    contrastWithWhite(value) >= QR_MIN_CONTRAST
  );
}

export interface QrStyle {
  color: string;
  logo: boolean;
  headline: string;
  callToAction: string;
  showPhone: boolean;
  labelFormat: QrLabelFormatId;
}

/** Réglages effectifs des QR codes : toute valeur absente ou refusée revient au défaut. */
export function qrStyle(branding: TenantBranding): QrStyle {
  return {
    color: isScannableQrColor(branding.qrColor) ? branding.qrColor.toLowerCase() : QR_DEFAULTS.color,
    logo: branding.qrLogo === true,
    headline: branding.qrHeadline?.trim() || QR_DEFAULTS.headline,
    callToAction: branding.qrCallToAction?.trim() || QR_DEFAULTS.callToAction,
    showPhone: branding.qrShowPhone !== false,
    labelFormat: isQrLabelFormat(branding.qrLabelFormat) ? branding.qrLabelFormat : QR_LABEL_FORMAT_IDS[0],
  };
}

/**
 * Options de génération du QR code : fond toujours blanc, et correction
 * d'erreurs maximale (H, 30 %) dès qu'un logo couvre le centre ; sinon Q, qui
 * tolère déjà une étiquette rayée ou salie.
 */
export function qrRenderOptions(style: Pick<QrStyle, 'color' | 'logo'>): {
  errorCorrectionLevel: 'Q' | 'H';
  color: { dark: string; light: string };
} {
  return {
    errorCorrectionLevel: style.logo ? 'H' : 'Q',
    color: {
      dark: isScannableQrColor(style.color) ? style.color : QR_DEFAULTS.color,
      light: '#ffffff',
    },
  };
}

export const DOCUMENT_STYLES = ['modern', 'classic', 'compact'] as const;
export type DocumentStyle = (typeof DOCUMENT_STYLES)[number];
export const LOGO_SIZES = ['small', 'medium', 'large'] as const;
export type LogoSize = (typeof LOGO_SIZES)[number];
/** Hauteur imprimée du logo, en millimètres. */
export const LOGO_SIZE_MM: Record<LogoSize, number> = { small: 12, medium: 18, large: 24 };
export const SALE_TYPES = ['biens', 'services', 'mixte'] as const;
export type SaleType = (typeof SALE_TYPES)[number];
export const SALE_TYPE_LABELS: Record<SaleType, string> = {
  biens: 'Livraisons de biens',
  services: 'Prestations de services',
  mixte: 'Livraisons de biens et prestations de services',
};

/** IBAN : format par pays et clé de contrôle (mod 97), espaces ignorés. */
export function normalizeIban(value: string): string {
  return value.replace(/\s/g, '').toUpperCase();
}
export function isValidIban(value: unknown): value is string {
  if (typeof value !== 'string' || !/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(value)) return false;
  const rearranged = `${value.slice(4)}${value.slice(0, 4)}`;
  let remainder = 0;
  for (const character of rearranged) {
    const digits = /\d/.test(character) ? character : String(character.charCodeAt(0) - 55);
    for (const digit of digits) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}
/** IBAN imprimé par groupes de quatre, comme sur un RIB. */
export function formatIban(value: string): string {
  return value.replace(/(.{4})/g, '$1 ').trim();
}
export function isValidBic(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Z]{6}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/.test(value);
}

/** Public company link; no executable schemes, credentials or control characters. */
export function isSafeWebsiteUrl(value: unknown): value is string {
  if (
    typeof value !== 'string' ||
    value.length > 2048 ||
    /\s/.test(value) ||
    [...value].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
  )
    return false;
  try {
    const url = new URL(value);
    return (
      ['https:', 'http:'].includes(url.protocol) &&
      Boolean(url.hostname) &&
      !url.username &&
      !url.password
    );
  } catch {
    return false;
  }
}

/** Couleur hexadécimale stricte : #rgb ou #rrggbb, rien d'autre. */
export function isSafeColor(value: unknown): value is string {
  return typeof value === 'string' && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value);
}

/**
 * URL de logo sûre : https absolu ou data:image inline. Tout le reste est
 * refusé — `javascript:`, `data:text/html`, http en clair… — car cette valeur
 * finit dans un attribut `src` rendu à des tiers (portail client, page QR).
 */
export function isSafeLogoUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 200_000) return false;
  // Image inline (base64 only : pas de SVG brut porteur de script inline)
  if (/^data:image\/(png|jpeg|webp);base64,[a-z0-9+/=]+$/i.test(value)) return true;
  // URL absolue https, sans caractère de contrôle ni espace
  return /^https:\/\/[\w.-]+(?::\d+)?(?:\/[^\s"'<>]*)?$/i.test(value);
}

function trimmedText(value: unknown, maximum: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  return text ? text.slice(0, maximum) : undefined;
}
function clippedText(value: unknown, maximum: number): string | undefined {
  return typeof value === 'string' ? value.slice(0, maximum) : undefined;
}
function safeEmail(value: unknown): string | undefined {
  return typeof value === 'string' &&
    /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(value) &&
    value.length <= 320
    ? value
    : undefined;
}
function safeLogoFileId(value: unknown): string | undefined {
  return typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
    ? value
    : undefined;
}
function safeDimension(value: unknown, minimum: number, maximum: number): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= minimum && value <= maximum
    ? value
    : undefined;
}
function isLogoAlignment(value: unknown): value is NonNullable<TenantBranding['logoAlignment']> {
  return value === 'left' || value === 'center' || value === 'right';
}
function isDocumentStyle(value: unknown): value is DocumentStyle {
  return typeof value === 'string' && (DOCUMENT_STYLES as readonly string[]).includes(value);
}
function isLogoSize(value: unknown): value is LogoSize {
  return typeof value === 'string' && (LOGO_SIZES as readonly string[]).includes(value);
}
function isSaleType(value: unknown): value is SaleType {
  return typeof value === 'string' && (SALE_TYPES as readonly string[]).includes(value);
}
function isQrLabelFormat(value: unknown): value is QrLabelFormatId {
  return typeof value === 'string' && (QR_LABEL_FORMAT_IDS as readonly string[]).includes(value);
}
function isVatNumberValue(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Z]{2}[A-Z0-9]{2,13}$/.test(value);
}
function isApeCode(value: unknown): value is string {
  return typeof value === 'string' && /^\d{2}\.?\d{2}[A-Z]$/.test(value);
}
function isPhone(value: unknown): value is string {
  return typeof value === 'string' && /^[+\d][\d\s.()-]{5,24}$/.test(value);
}
function validatedValue<T>(
  value: unknown,
  validate: (value: unknown) => value is T,
): T | undefined {
  return validate(value) ? value : undefined;
}
function assignDefined<K extends keyof TenantBranding>(
  output: TenantBranding,
  key: K,
  value: TenantBranding[K] | undefined,
): void {
  if (value !== undefined) output[key] = value;
}
/** Preserve only safe, bounded company identity values; missing values stay absent. */
export function sanitizeTenantBranding(raw: unknown): TenantBranding {
  if (!raw || typeof raw !== 'object') return {};
  const input = raw as Record<string, unknown>;
  const output: TenantBranding = {};
  assignDefined(output, 'displayName', trimmedText(input.displayName, BRANDING_TEXT_LIMITS.displayName));
  assignDefined(output, 'websiteUrl', validatedValue(input.websiteUrl, isSafeWebsiteUrl));
  assignDefined(output, 'primaryColor', validatedValue(input.primaryColor, isSafeColor));
  assignDefined(output, 'accentColor', validatedValue(input.accentColor, isSafeColor));
  assignDefined(output, 'logoUrl', validatedValue(input.logoUrl, isSafeLogoUrl));
  assignDefined(output, 'logoFileId', safeLogoFileId(input.logoFileId));
  assignDefined(output, 'documentFooter', clippedText(input.documentFooter, BRANDING_TEXT_LIMITS.documentFooter));
  assignDefined(output, 'emailSignature', clippedText(input.emailSignature, BRANDING_TEXT_LIMITS.emailSignature));
  assignDefined(output, 'replyToEmail', safeEmail(input.replyToEmail));
  assignDefined(output, 'documentEmail', safeEmail(input.documentEmail));
  assignDefined(output, 'documentAddress', clippedText(input.documentAddress, BRANDING_TEXT_LIMITS.documentAddress));
  assignDefined(output, 'paymentTerms', trimmedText(input.paymentTerms, BRANDING_TEXT_LIMITS.paymentTerms));
  assignDefined(output, 'latePenalty', trimmedText(input.latePenalty, BRANDING_TEXT_LIMITS.latePenalty));
  assignDefined(output, 'fixedIndemnity', trimmedText(input.fixedIndemnity, BRANDING_TEXT_LIMITS.fixedIndemnity));
  assignDefined(output, 'logoWidthMm', safeDimension(input.logoWidthMm, 20, 80));
  assignDefined(output, 'logoHeightMm', safeDimension(input.logoHeightMm, 10, 40));
  assignDefined(output, 'logoAlignment', validatedValue(input.logoAlignment, isLogoAlignment));
  assignDefined(output, 'documentStyle', validatedValue(input.documentStyle, isDocumentStyle));
  assignDefined(output, 'logoSize', validatedValue(input.logoSize, isLogoSize));
  assignDefined(output, 'documentPhone', validatedValue(input.documentPhone, isPhone));
  assignDefined(output, 'legalForm', trimmedText(input.legalForm, BRANDING_TEXT_LIMITS.legalForm));
  assignDefined(output, 'shareCapital', trimmedText(input.shareCapital, BRANDING_TEXT_LIMITS.shareCapital));
  assignDefined(output, 'registration', trimmedText(input.registration, BRANDING_TEXT_LIMITS.registration));
  assignDefined(output, 'vatNumber', validatedValue(input.vatNumber, isVatNumberValue));
  assignDefined(output, 'apeCode', validatedValue(input.apeCode, isApeCode));
  assignDefined(output, 'insurance', trimmedText(input.insurance, BRANDING_TEXT_LIMITS.insurance));
  assignDefined(output, 'paymentMethod', trimmedText(input.paymentMethod, BRANDING_TEXT_LIMITS.paymentMethod));
  assignDefined(output, 'bankName', trimmedText(input.bankName, BRANDING_TEXT_LIMITS.bankName));
  assignDefined(output, 'iban', validatedValue(input.iban, isValidIban));
  assignDefined(output, 'bic', validatedValue(input.bic, isValidBic));
  if (typeof input.showPaymentReference === 'boolean') output.showPaymentReference = input.showPaymentReference;
  assignDefined(output, 'discountTerms', trimmedText(input.discountTerms, BRANDING_TEXT_LIMITS.discountTerms));
  assignDefined(output, 'saleType', validatedValue(input.saleType, isSaleType));
  assignDefined(output, 'specialMention', trimmedText(input.specialMention, BRANDING_TEXT_LIMITS.specialMention));
  assignDefined(output, 'quoteTerms', trimmedText(input.quoteTerms, BRANDING_TEXT_LIMITS.quoteTerms));
  assignDefined(output, 'qrColor', validatedValue(input.qrColor, isScannableQrColor));
  if (typeof input.qrLogo === 'boolean') output.qrLogo = input.qrLogo;
  assignDefined(output, 'qrHeadline', trimmedText(input.qrHeadline, BRANDING_TEXT_LIMITS.qrHeadline));
  assignDefined(output, 'qrCallToAction', trimmedText(input.qrCallToAction, BRANDING_TEXT_LIMITS.qrCallToAction));
  if (typeof input.qrShowPhone === 'boolean') output.qrShowPhone = input.qrShowPhone;
  assignDefined(output, 'qrLabelFormat', validatedValue(input.qrLabelFormat, isQrLabelFormat));
  return output;
}

/**
 * Ce qu'une coque externe (portail client, pages publiques, espace
 * sous-traitant) peut montrer de l'entreprise : nom, logo, couleurs et site.
 * Jamais les coordonnées bancaires, l'adresse de réponse ni les mentions
 * légales du branding complet (minimisation des données exposées).
 */
export type ShellBranding = Pick<
  TenantBranding,
  'displayName' | 'websiteUrl' | 'logoUrl' | 'logoFileId' | 'primaryColor' | 'accentColor'
>;

const SHELL_BRANDING_KEYS = [
  'displayName', 'websiteUrl', 'logoUrl', 'logoFileId', 'primaryColor', 'accentColor',
] as const satisfies readonly (keyof ShellBranding)[];

/** Projection sûre du branding pour une coque externe (valeurs validées comme `sanitizeTenantBranding`). */
export function shellBranding(raw: unknown): ShellBranding {
  const safe = sanitizeTenantBranding(raw);
  const output: ShellBranding = {};
  for (const key of SHELL_BRANDING_KEYS) {
    const value = safe[key];
    if (value !== undefined) output[key] = value;
  }
  return output;
}
